/**
 * Charts, built to the dataviz method: form first, colour last, validated palette (tokens.css).
 * One series colour for the firm, a neutral tick for the network median, a single-hue ramp for the
 * heatmap. Every chart has a hover/focus tooltip and a table view; numbers wear text colours.
 */
import { useId, useState, type ReactNode } from 'react';
import { useWide } from './Explained';

const fmtPct = (v: number | null | undefined) => (v === null || v === undefined ? 'No results' : `${Math.round(v)}%`);

/** Shared tooltip: follows the hovered or focused mark within its chart. */
function useTip() {
  const [tip, setTip] = useState<{ x: number; y: number; text: ReactNode } | null>(null);
  const bind = (text: ReactNode) => ({
    onMouseMove: (e: React.MouseEvent<HTMLElement>) => {
      const box = e.currentTarget.closest('.chart')!.getBoundingClientRect();
      setTip({ x: e.clientX - box.left, y: e.clientY - box.top, text });
    },
    onMouseLeave: () => setTip(null),
  });
  const node = tip && (
    <div className="chart-tip" role="presentation" style={{ left: tip.x, top: tip.y }}>
      {tip.text}
    </div>
  );
  return { bind, node };
}

/** "Show as table": the accessible, printable twin of every chart. */
export function ChartTable({ caption, head, rows }: { caption: string; head: string[]; rows: ReactNode[][] }) {
  return (
    <details className="chart-table">
      <summary>Show as table</summary>
      <div className="table-wrap" tabIndex={0} role="region" aria-label={`${caption}, table`}>
        <table>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {head.map((h) => (
                <th key={h} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (j === 0 ? <th key={j} scope="row">{c}</th> : <td key={j}>{c}</td>))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * In place in two parts (Andres, 2026-09-26): of a share in place `pct`, `partly` points are only partly in place.
 * Both rounded so they add up to the rounded headline: 100% in place = 64% fully + 36% partly.
 */
export function inPlaceParts(pct: number | null | undefined, partly: number | null | undefined): { fully: number; partly: number } | null {
  if (pct === null || pct === undefined) return null;
  const total = Math.round(pct);
  const fully = Math.round(pct - Math.max(0, partly ?? 0));
  return { fully, partly: total - fully };
}

/** "64% fully": shown directly under or beside every in-place headline, so 100% never reads as done. */
export const fullyText = (pct: number | null | undefined, partly: number | null | undefined) => {
  const p = inPlaceParts(pct, partly);
  return p ? `${p.fully}% fully` : '';
};

/** "100% in place: 64% fully, 36% partly", for tooltips and screen readers. */
export const inPlaceSummary = (pct: number | null | undefined, partly: number | null | undefined) => {
  const p = inPlaceParts(pct, partly);
  return p ? `${Math.round(pct!)}% in place: ${p.fully}% fully, ${p.partly}% partly` : 'No results';
};

/** Legend for a chart that draws in place in two parts. Keys carry the colour; the words stay in text colours. */
/** A table's in-place column: "BTC Baseline, in place", but never "Critical protections in place, in place". */
const inPlaceHead = (caption: string) => (/in place$/.test(caption) ? caption : `${caption}, in place`);

export function InPlaceKey({ others, median, survey, count }: { others?: boolean; median?: boolean; survey?: boolean; /** Names plum (--chart-count, DEC-28) with what it counts on this tab. */ count?: boolean | string }) {
  return (
    // UX-12 (5E pre-gate): each swatch and its words in one unbreakable item.
    <p className="legend small in-place-key">
      <span className="key-item">
        <span className="key-fully" aria-hidden="true" /> Fully in place
      </span>{' '}
      <span className="key-item">
        <span className="key-partly" aria-hidden="true" /> Partly in place
      </span>
      {median && (
        <>
          {' '}
          <span className="key-item">
            <span className="key-median" aria-hidden="true" /> Network median
          </span>
        </>
      )}
      {survey && (
        <>
          {' '}
          <span className="key-item">
            <span className="key-survey" aria-hidden="true" /> March 2025 survey answers
          </span>
        </>
      )}
      {count && (
        <>
          {' '}
          <span className="key-item">
            <span className="key-firm key-count" aria-hidden="true" /> {typeof count === 'string' ? count : 'Plans or firms counted'}
          </span>
        </>
      )}
      {others && <span className="muted"> Other firms are grey, with partly in place hatched.</span>}
    </p>
  );
}

/**
 * A 0-100% bar with an optional network-median tick. With `partly` (points of pct only partly in place) the fill
 * is drawn in two parts: fully in place solid, partly in place hatched in its own colour.
 */
export function Bullet({ pct, partly, median, label }: { pct: number | null; partly?: number | null; median?: number | null; label: string }) {
  const total = pct === null ? 0 : Math.min(pct, 100);
  const part = Math.min(Math.max(0, partly ?? 0), total);
  const full = total - part;
  return (
    <div className="bullet" role="img" aria-label={label}>
      {full > 0 && <div className={`bullet-fill${part > 0 ? ' has-partly' : ''}`} style={{ width: `${full}%` }} />}
      {part > 0 && <div className={`bullet-partly${full > 0 ? '' : ' alone'}`} style={{ left: `${full}%`, width: `${part}%` }} />}
      {median !== null && median !== undefined && <div className="bullet-median" style={{ left: `${median}%` }} />}
    </div>
  );
}

/** "Includes 14 partly in place": what a headline "in place" count is made of, in plain words. */
export const partlyText = (partly: number) => (partly === 0 ? 'None only partly in place' : `Includes ${partly} partly in place`);

/**
 * In place counts controls fully or partly in place (Andres, 2026-09-26). This bar sits directly under each
 * headline so the share only partly done stays in view: fully (solid), partly (hatched), not yet (track) and,
 * where relevant, not rated (outlined). Status colours only; never red.
 */
export function InPlaceSplit({ fully, partly, notYet, notRated = 0, label, median = null }: {
  fully: number;
  partly: number;
  notYet: number;
  notRated?: number;
  label: string;
  /** Network median (% in place of rated controls), drawn as a tick; only meaningful when nothing is unrated. */
  median?: number | null;
}) {
  const total = fully + partly + notYet + notRated;
  const segs = (
    [
      ['fully', fully, 'Fully in place'],
      ['partly', partly, 'Partly in place'],
      ['not-yet', notYet, 'Not yet'],
      ['not-rated', notRated, 'Not rated'],
    ] as const
  ).filter(([k, n]) => n > 0 || k !== 'not-rated');
  return (
    <div className="split">
      <div
        className="split-track"
        role="img"
        aria-label={`${label}: ${fully} fully in place, ${partly} partly in place, ${notYet} not yet in place${notRated ? `, ${notRated} not rated` : ''}${median !== null ? `, network median ${fmtPct(median)}` : ''}`}
      >
        <div className="split-bar">
          {total > 0 && segs.filter(([, n]) => n > 0).map(([k, n]) => <span key={k} className={`split-seg ${k}`} style={{ width: `${(n / total) * 100}%` }} />)}
        </div>
        {median !== null && <div className="bullet-median split-median" style={{ left: `${median}%` }} />}
      </div>
      <p className="split-legend small" aria-hidden="true">
        {segs.map(([k, n, name]) => (
          <span key={k}>
            <span className={`split-key ${k}`} /> {name} {n}
            
          </span>
        ))}
      </p>
    </div>
  );
}

/** Hero tile: one named baseline, the firm's %, and where the network median sits. */
export function BaselineTile({ name, what, pct, inPlace, assessed, median }: {
  name: string;
  what: string;
  pct: number | null;
  inPlace: number;
  assessed: number;
  median: number | null;
}) {
  return (
    <figure className="measure baseline">
      <figcaption>
        <span className="baseline-name">{name}</span>
        <span className="muted small">
          {inPlace} {what}
        </span>
      </figcaption>
      <div className="measure-value hero">{fmtPct(pct)}</div>
      <Bullet
        pct={pct}
        median={median}
        label={`${name}: ${fmtPct(pct)}${median !== null ? `, network median ${fmtPct(median)}` : ''}. ${inPlace} of ${assessed} in place.`}
      />
      {median !== null && (
        <p className="legend small">
          <span className="key-median" aria-hidden="true" /> Network median {fmtPct(median)}
        </p>
      )}
    </figure>
  );
}

export interface AreaScore {
  domainNumber: string;
  area: string;
  pct: number | null;
  inPlace: number;
  assessed: number;
  networkMedian: number | null;
}

/** Horizontal bars by area: the firm (teal) against the network median (tick), in guidance order. */
export function AreaBars({ areas, caption }: { areas: AreaScore[]; caption: string }) {
  const { bind, node } = useTip();
  const hasMedian = areas.some((a) => a.networkMedian !== null);
  return (
    <div className="chart">
      <div className="legend small" aria-hidden="true">
        <span className="key-firm" /> Your firm
        {hasMedian && (
          <>
            <span className="key-median" /> Network median
          </>
        )}
      </div>
      <ul className="bars" aria-label={caption}>
        {areas.map((a) => (
          <li key={a.domainNumber} {...bind(<><strong>{a.area}</strong><br />{a.inPlace} of {a.assessed} in place ({fmtPct(a.pct)}){a.networkMedian !== null && <><br />Network median {fmtPct(a.networkMedian)}</>}</>)}>
            <span className="bar-label">{a.area}</span>
            <Bullet pct={a.pct} median={a.networkMedian} label={`${a.area}: ${fmtPct(a.pct)}${a.networkMedian !== null ? `, network median ${fmtPct(a.networkMedian)}` : ''}`} />
            <span className="bar-value">{fmtPct(a.pct)}</span>
          </li>
        ))}
      </ul>
      {node}
      <ChartTable
        caption={caption}
        head={['Area', 'In place', 'Your firm', 'Network median']}
        rows={areas.map((a) => [a.area, `${a.inPlace} of ${a.assessed}`, fmtPct(a.pct), fmtPct(a.networkMedian)])}
      />
    </div>
  );
}

/** How a ranked bar's value was reached, when it is the firm's own report rather than an independent result. */
const MARK = {
  self: { cls: 'marked', short: 'self', tip: 'Self-assessed, waiting for CDW', aria: 'self-assessed', source: 'Self-assessed, 2026', key: 'key-hatch', legend: 'Self-assessed while waiting for CDW. Not in the network median.' },
  survey: {
    cls: 'survey',
    short: '2025',
    tip: "As of March 2025: the firm's own survey answers",
    aria: "March 2025 survey, the firm's own answers",
    source: "March 2025 survey, the firm's own answers",
    key: 'key-hatch survey',
    legend: 'March 2025 survey answers. No 2026 rating yet. Not in the network median.',
  },
} as const;
const markOf = (m: boolean | 'survey' | undefined) => (m === 'survey' ? MARK.survey : m ? MARK.self : null);

/** Firms ranked on one measure, highest first; firms without results listed last, unfilled. */
export function RankedBars({ caption, rows, format = fmtPct, max = 100, median }: {
  caption: string;
  /**
   * marked: a firm's own report rather than an independent result, drawn hatched and labelled: true for its 2026
   * self-assessment, 'survey' for its March 2025 survey answers. you: the firm being looked at, the only coloured bar.
   */
  rows: Array<{ key: string; label: string; value: number | null; marked?: boolean | 'survey'; you?: boolean; partly?: number | null }>;
  format?: (v: number | null) => string;
  max?: number;
  /** Network median, drawn as a tick on every bar. */
  median?: number | null;
}) {
  const { bind, node } = useTip();
  const sorted = [...rows].sort((a, b) => (b.value ?? -1) - (a.value ?? -1));
  const hasYou = rows.some((r) => r.you);
  // Percentages only: with partly given, each bar is fully in place solid and partly in place hatched, and the
  // value reads "100% (64% fully)" so a firm at 100% in place never reads as done (Andres, 2026-09-26).
  const split = max === 100 && rows.some((r) => r.value !== null && r.partly !== undefined && r.partly !== null);
  // A row without a partly figure (the March 2025 survey only knew achieved or not) is drawn as one fill.
  const partlyOf = (r: (typeof rows)[number]) => (split && r.value !== null && r.partly !== undefined && r.partly !== null ? r.partly : null);
  return (
    <div className="chart">
      {split && <InPlaceKey others={hasYou} />}
      <ul className={`bars ranked${hasYou ? ' has-you' : ''}${split ? ' with-fully' : ''}`} aria-label={caption}>
        {sorted.map((r) => {
          const m = markOf(r.marked);
          const p = partlyOf(r);
          const detail = p !== null ? inPlaceSummary(r.value, p) : format(r.value);
          return (
            <li key={r.key} className={[m?.cls, r.you && 'you'].filter(Boolean).join(' ') || undefined} {...bind(<><strong>{r.label}</strong><br />{detail}{m && <><br />{m.tip}</>}</>)}>
              <span className="bar-label">
                {r.label}
                {m && <span className="muted"> ({m.short})</span>}
              </span>
              <Bullet pct={r.value === null ? null : (r.value / max) * 100} partly={p} median={median} label={`${r.label}: ${detail}${m ? `, ${m.aria}` : ''}`} />
              <span className="bar-value">
                {format(r.value)}
                {p !== null && p > 0 && <span className="bar-fully"> ({fullyText(r.value, p)})</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {node}
      {Object.values(MARK)
        .filter((k) => sorted.some((r) => markOf(r.marked) === k))
        .map((k) => (
          <p key={k.cls} className="legend small">
            <span className={k.key} aria-hidden="true" /> {k.legend}
          </p>
        ))}
      <ChartTable
        caption={caption}
        head={split ? ['Firm', inPlaceHead(caption), 'Fully in place', 'Partly in place', 'Source'] : ['Firm', caption, 'Source']}
        rows={sorted.map((r) => {
          const p = partlyOf(r);
          const parts = inPlaceParts(r.value, p);
          const source = markOf(r.marked)?.source ?? 'CDW, 2026';
          return split ? [r.label, format(r.value), p !== null && parts ? `${parts.fully}%` : '', p !== null && parts ? `${parts.partly}%` : '', source] : [r.label, format(r.value), source];
        })}
      />
    </div>
  );
}

/**
 * Anonymous firms ranked on one measure, as columns highest first: firms that sit close together stay
 * readable as neighbouring columns instead of overlapping dots. Only the viewer's firm is coloured and
 * labelled; the network median is a line across.
 */
export function RankedColumns({ caption, values, median, format = fmtPct }: {
  caption: string;
  /**
   * partly: points of value only partly in place. Each column is then a small stack, fully in place below and
   * partly in place hatched on top, so the column height still compares in place across firms while the share
   * with work still to do stays visible (a two-tone dot could not show that proportion).
   */
  values: Array<{ value: number; you?: boolean; partly?: number | null }>;
  median: number | null;
  format?: (v: number | null) => string;
}) {
  const { bind, node } = useTip();
  const sorted = [...values].sort((a, b) => b.value - a.value);
  const you = sorted.findIndex((v) => v.you);
  const split = sorted.some((v) => v.partly !== undefined && v.partly !== null);
  const detail = (v: (typeof values)[number]) => (v.partly !== undefined && v.partly !== null ? inPlaceSummary(v.value, v.partly) : format(v.value));
  return (
    <div className="chart">
      {split && <InPlaceKey others />}
      <div className="cols" role="img" aria-label={`${caption}: ${sorted.length} firms, highest ${format(sorted[0]?.value ?? null)}, lowest ${format(sorted.at(-1)?.value ?? null)}${you >= 0 ? `, your firm ${detail(sorted[you]!)}, ${ordinal(you + 1)} of ${sorted.length}` : ''}${median !== null ? `, median ${format(median)}` : ''}`}>
        <span className="cols-grid" style={{ bottom: '50%' }} aria-hidden="true" />
        <span className="cols-axis-label" style={{ bottom: '100%' }} aria-hidden="true">100%</span>
        <span className="cols-axis-label" style={{ bottom: '50%' }} aria-hidden="true">50%</span>
        {sorted.map((v, i) => {
          const total = Math.max(1, Math.min(100, v.value));
          const part = Math.min(total, Math.max(0, v.partly ?? 0));
          return (
            <span key={i} className={`col${v.you ? ' you' : ''}`} {...bind(<><strong>{v.you ? 'Your firm' : 'A firm'}</strong><br />{detail(v)}</>)}>
              {part > 0 && <span className={`col-partly${total - part > 0 ? '' : ' alone'}`} style={{ height: `${part}%` }} />}
              {total - part > 0 && <span className={`col-fill${part > 0 ? ' has-partly' : ''}`} style={{ height: `${total - part}%` }} />}
              {v.you && <span className="col-you-label">You</span>}
            </span>
          );
        })}
        {median !== null && (
          <span className="cols-median" style={{ bottom: `${median}%` }} aria-hidden="true">
            <span>Median {format(median)}</span>
          </span>
        )}
      </div>
      {node}
      {you >= 0 && sorted[you]!.partly ? (
        <p className="small cols-you-value">
          Your firm: <strong>{format(sorted[you]!.value)} in place</strong> ({fullyText(sorted[you]!.value, sorted[you]!.partly)})
        </p>
      ) : null}
      <p className="legend small">
        <span className="dot-key you" aria-hidden="true" /> Your firm <span className="dot-key" aria-hidden="true" /> Other firms, not named <span className="key-median" aria-hidden="true" /> Network median
      </p>
      <ChartTable
        caption={caption}
        head={split ? ['Rank', inPlaceHead(caption), 'Fully in place', 'Partly in place'] : ['Rank', caption]}
        rows={sorted.map((v, i) => {
          const rank = v.you ? `${i + 1} (your firm)` : String(i + 1);
          const parts = v.partly !== undefined && v.partly !== null ? inPlaceParts(v.value, v.partly) : null;
          return split ? [rank, format(v.value), parts ? `${parts.fully}%` : '', parts ? `${parts.partly}%` : ''] : [rank, format(v.value)];
        })}
      />
    </div>
  );
}

export const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;

export interface ThenNowRow {
  domainNumber: string;
  area: string;
  then2025: number | null;
  now2026: number | null;
  controls: number;
}

/**
 * March 2025 against 2026 by area: a hollow dot for the survey (the firm's own report) and a solid dot for
 * 2026, joined by a thin line. Position encodes the share in place; the two dots differ in fill, not hue.
 */
export function ThenNow({ rows, caption, nowLabel }: { rows: ThenNowRow[]; caption: string; nowLabel: string }) {
  const { bind, node } = useTip();
  return (
    <div className="chart">
      <div className="legend small" aria-hidden="true">
        <span className="dot then" /> March 2025, reported by the firm
        <span className="dot now" /> {nowLabel}
      </div>
      <ul className="bars thennow" aria-label={caption}>
        {rows.map((r) => {
          const a = r.then2025 ?? 0;
          const b = r.now2026 ?? 0;
          return (
            <li key={r.domainNumber} {...bind(<><strong>{r.area}</strong><br />March 2025: {fmtPct(r.then2025)}<br />{nowLabel}: {fmtPct(r.now2026)}<br /><span className="muted">{r.controls} surveyed controls</span></>)}>
              <span className="bar-label">{r.area}</span>
              <div className="dumbbell" role="img" aria-label={`${r.area}: March 2025 ${fmtPct(r.then2025)}, ${nowLabel} ${fmtPct(r.now2026)}`}>
                {r.then2025 !== null && r.now2026 !== null && <span className="link" style={{ left: `${Math.min(a, b)}%`, width: `${Math.abs(b - a)}%` }} />}
                {r.then2025 !== null && <span className="dot then" style={{ left: `${a}%` }} />}
                {r.now2026 !== null && <span className="dot now" style={{ left: `${b}%` }} />}
              </div>
              <span className="bar-value">{fmtPct(r.now2026)}</span>
            </li>
          );
        })}
      </ul>
      {node}
      <ChartTable
        caption={caption}
        head={['Area', 'March 2025', nowLabel, 'Surveyed controls']}
        rows={rows.map((r) => [r.area, fmtPct(r.then2025), fmtPct(r.now2026), String(r.controls)])}
      />
    </div>
  );
}

const BINS = [20, 40, 60, 80, 90];
const binOf = (v: number) => 1 + BINS.filter((b) => v >= b).length;
const BIN_LABELS = ['Under 20%', '20 to 39%', '40 to 59%', '60 to 79%', '80 to 89%', '90% or more'];

/** Firms (rows) by area (columns): share of each area's controls in place. Single-hue ramp. */
export function Heatmap({ caption, firms, areas }: {
  caption: string;
  /** partly: per area, points of the in-place share only partly in place; drawn as a hatched strip along the cell's foot. */
  firms: Array<{ key: string; label: string; values: Record<string, number | null>; partly?: Record<string, number | null> }>;
  areas: Array<{ key: string; label: string }>;
}) {
  const { bind, node } = useTip();
  const id = useId();
  const hasPartly = firms.some((f) => f.partly && Object.values(f.partly).some((v) => (v ?? 0) > 0));
  const cellText = (f: (typeof firms)[number], key: string) => {
    const v = f.values[key];
    const p = f.partly?.[key];
    return v !== null && v !== undefined && p !== null && p !== undefined ? `${fmtPct(v)} (${fullyText(v, p)})` : fmtPct(v);
  };
  return (
    <div className="chart">
      <div className="heat-scroll" tabIndex={0} role="region" aria-label={caption}>
        <div className="heatmap" role="img" aria-labelledby={`${id}-cap`} style={{ gridTemplateColumns: `minmax(120px, 1.4fr) repeat(${areas.length}, minmax(22px, 1fr))` }}>
          <span id={`${id}-cap`} className="sr-only">
            {caption}. The table view below lists every value.
          </span>
          <span />
          {areas.map((a) => (
            <span key={a.key} className="heat-col" title={a.label}>
              {a.label}
            </span>
          ))}
          {firms.map((f) => (
            <FirmRow key={f.key} firm={f} areas={areas} bind={bind} />
          ))}
        </div>
      </div>
      <ol className="heat-legend small" aria-label="Colour key">
        {BIN_LABELS.map((l, i) => (
          <li key={l}>
            <span className={`heat-swatch heat-${i + 1}`} aria-hidden="true" /> {l}
          </li>
        ))}
        <li>
          <span className="heat-swatch heat-none" aria-hidden="true" /> No results yet
        </li>
        {hasPartly && (
          <li>
            <span className="heat-swatch heat-partly-key" aria-hidden="true" /> Strip along the foot: share partly in place
          </li>
        )}
      </ol>
      {node}
      <ChartTable
        caption={caption}
        head={['Firm', ...areas.map((a) => a.label)]}
        rows={firms.map((f) => [f.label, ...areas.map((a) => cellText(f, a.key))])}
      />
    </div>
  );
}

function FirmRow({ firm, areas, bind }: {
  firm: { label: string; values: Record<string, number | null>; partly?: Record<string, number | null> };
  areas: Array<{ key: string; label: string }>;
  bind: (t: ReactNode) => object;
}) {
  return (
    <>
      <span className="heat-row">{firm.label}</span>
      {areas.map((a) => {
        const v = firm.values[a.key];
        const p = firm.partly?.[a.key];
        const cls = v === null || v === undefined ? 'heat-none' : `heat-${binOf(v)}`;
        const text = v !== null && v !== undefined && p !== null && p !== undefined ? inPlaceSummary(v, p) : fmtPct(v);
        return (
          <span key={a.key} className={`heat-cell ${cls}`} {...bind(<><strong>{firm.label}</strong><br />{a.label}: {text}</>)}>
            {v !== null && v !== undefined && (p ?? 0) > 0 && <span className="heat-partly" style={{ width: `calc((100% - 4px) * ${Math.min(100, p!) / 100})` }} />}
          </span>
        );
      })}
    </>
  );
}

/**
 * A baseline as a KPI ring: the share in place as an arc, the network median as a tick on the ring, and
 * the count in the middle. One series colour; the median wears the neutral ink.
 */
export function Ring({ pct, median, label, size = 132, text, hatched, partly, sub, tone, tight }: {
  pct: number | null;
  median: number | null;
  label: string;
  size?: number;
  text?: string;
  /** The firm's own report (the March 2025 survey), drawn hatched so it never reads as an independent result. */
  hatched?: boolean;
  /**
   * Points of pct only partly in place: the arc is then fully in place (solid) followed by partly in place
   * (its own colour, hatched), and the label says both.
   */
  partly?: number | null;
  /** A second line under the centre value, e.g. "64% fully". */
  sub?: string;
  /** "count" draws the arc in the neutral not-yet colour: for counts that are not "in place" (teal means fully in place). */
  tone?: 'count';
  /**
   * The value and its second line set closer together, for a small ring whose CSS sets smaller label fonts, so both
   * sit inside the 12-unit arc (5C NEXT PASS 5: "N% fully" overlapped the arc on Leadership's tiles).
   */
  tight?: boolean;
}) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const hatchId = `ring-hatch-${uid}`;
  const partlyId = `ring-partly-${uid}`;
  const r = size / 2 - 10;
  const c = 2 * Math.PI * r;
  const at = (p: number) => {
    const a = (p / 100) * 2 * Math.PI - Math.PI / 2;
    return { x: size / 2 + Math.cos(a) * r, y: size / 2 + Math.sin(a) * r, a };
  };
  const m = median === null ? null : at(median);
  const total = pct === null ? 0 : Math.min(Math.max(pct, 0), 100);
  const part = hatched ? 0 : Math.min(total, Math.max(0, partly ?? 0));
  const full = total - part;
  // A 2px surface gap between the two parts (and before the start when the ring is full).
  const gap = part > 0 && full > 0 ? 2 : 0;
  const fullLen = (full / 100) * c;
  const partLen = (part / 100) * c;
  const small = size < 110;
  return (
    <svg className={hatched ? 'ring hatched' : 'ring'} width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label}>
      {hatched && (
        <defs>
          <pattern id={hatchId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="var(--surface)" />
            <rect width="2.5" height="6" fill="var(--chart-firm)" />
          </pattern>
        </defs>
      )}
      {part > 0 && (
        <defs>
          <pattern id={partlyId} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(135)">
            <rect width="5" height="5" fill="var(--surface)" />
            <rect width="3" height="5" fill="var(--chart-partly)" />
          </pattern>
        </defs>
      )}
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--chart-track)" strokeWidth="12" />
      {part > 0 ? (
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {full > 0 && (
            <circle className="ring-fully" cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--chart-fully)" strokeWidth="12" strokeDasharray={`${Math.max(0, fullLen - gap)} ${c}`} />
          )}
          <circle
            className="ring-partly"
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={`url(#${partlyId})`}
            strokeWidth="12"
            strokeDasharray={`${Math.max(0, partLen - (total >= 100 ? gap : 0))} ${c}`}
            strokeDashoffset={-fullLen}
          />
        </g>
      ) : pct !== null && pct > 0 && (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={hatched ? `url(#${hatchId})` : tone === 'count' ? 'var(--chart-count)' : 'var(--chart-firm)'}
          strokeWidth="12"
          strokeLinecap={hatched ? 'butt' : 'round'}
          strokeDasharray={`${(Math.min(pct, 100) / 100) * c} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      )}
      {m && (
        <line
          x1={size / 2 + Math.cos(m.a) * (r - (sub ? 4 : 11))}
          y1={size / 2 + Math.sin(m.a) * (r - (sub ? 4 : 11))}
          x2={size / 2 + Math.cos(m.a) * (r + 11)}
          y2={size / 2 + Math.sin(m.a) * (r + 11)}
          stroke="var(--chart-median)"
          strokeWidth="3"
          strokeLinecap="round"
        />
      )}
      <text x="50%" y={sub ? `${50 - (tight ? 8 : small ? 8 : 7)}%` : '50%'} textAnchor="middle" dominantBaseline="central" className="ring-value">
        {text ?? (pct === null ? 'n/a' : `${Math.round(pct)}%`)}
      </text>
      {sub && (
        <text x="50%" y={`${50 + (tight ? 11 : small ? 13 : 12)}%`} textAnchor="middle" dominantBaseline="central" className="ring-sub">
          {sub}
        </text>
      )}
    </svg>
  );
}

export interface RoadmapItem {
  id: number;
  code: string;
  label: string;
  start: string | null;
  target: string | null;
  progress: number | null;
  delivery: string;
  deliveryLabel: string;
}

const MONTH = 24 * 3600 * 1000 * 30.44;

/**
 * Roadmap: each action plan as a bar from start to target date, filled to its progress, coloured by
 * delivery status (red only for off track, per the Board convention). Plans without dates are listed
 * below the timeline. A table view carries every value.
 */
export function Roadmap({ items, caption, today = new Date() }: { items: RoadmapItem[]; caption: string; today?: Date }) {
  const { bind, node } = useTip();
  const phone = !useWide(600);
  const dated = items.filter((i) => i.target);
  const undated = items.filter((i) => !i.target);
  const t = (s: string) => new Date(`${s}T00:00:00Z`).getTime();
  const min = Math.min(today.getTime(), ...dated.map((i) => t(i.start ?? i.target!)));
  const max = Math.max(today.getTime() + MONTH, ...dated.map((i) => t(i.target!)));
  const from = new Date(new Date(min).getUTCFullYear(), new Date(min).getUTCMonth(), 1).getTime();
  const span = Math.max(max - from, MONTH * 3);
  const x = (ms: number) => ((ms - from) / span) * 100;
  const months: Date[] = [];
  for (let d = new Date(from); d.getTime() <= from + span; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) months.push(d);
  const fmt = (s: string | null) => (s ? new Date(`${s}T00:00:00Z`).toLocaleDateString('en-CA', { month: 'short', year: 'numeric', timeZone: 'UTC' }) : 'Not set');
  // 5E NEXT PASS 5 (E8): on a phone the axis has room for about four month names, so every nth month is named (the
  // year with the first one named and with each new year), and a month too near the right end for its name to fit is
  // left unnamed, so no name runs past the card. The table view below gives every date.
  // Shortcut: the step and the end room assume a phone-width axis (about 220 px) under 600 px; revisit if the roadmap
  // sits in a narrow column on a wide screen.
  const step = phone ? Math.max(1, Math.ceil(months.length / 4)) : 1;
  const named = months.map((m, i) => ({ m, i })).filter(({ m, i }) => i === 0 || (i % step === 0 && x(m.getTime()) <= (phone ? 80 : 95)));
  return (
    <div className="chart">
      {dated.length > 0 && (
        <div className="roadmap" role="img" aria-label={`${caption}. The table view below lists every plan.`}>
          <div className="roadmap-axis" aria-hidden="true">
            {named.map(({ m, i }, k) => {
              const at = x(m.getTime());
              const year = k === 0 || m.getFullYear() !== named[k - 1]!.m.getFullYear();
              return (
                <span key={i} style={{ left: `${at}%` }}>
                  {year ? m.toLocaleDateString('en-CA', { month: 'short', year: '2-digit' }) : m.toLocaleDateString('en-CA', { month: 'short' })}
                </span>
              );
            })}
          </div>
          {dated.map((i) => {
            const s = x(t(i.start ?? i.target!));
            const e = x(t(i.target!));
            return (
              <div className="roadmap-row" key={i.id} {...bind(<><strong>{i.code}</strong> {i.label}<br />{fmt(i.start)} to {fmt(i.target)}<br />{i.progress ?? 0}% done · {i.deliveryLabel}</>)}>
                <span className="roadmap-label">{i.code}</span>
                <div className="roadmap-track">
                  <span className="roadmap-today" style={{ left: `${x(today.getTime())}%` }} aria-hidden="true" />
                  <div className={`roadmap-bar d-${i.delivery}`} style={{ left: `${s}%`, width: `${Math.max(e - s, 1.5)}%` }}>
                    <span className="roadmap-fill" style={{ width: `${i.progress ?? 0}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {undated.length > 0 && <p className="muted small">No dates yet: {undated.map((i) => i.code).join(', ')}.</p>}
      {node}
      <ChartTable
        caption={caption}
        head={['Plan', 'Objective', 'Start', 'Target', 'Progress', 'Status']}
        rows={items.map((i) => [i.code, i.label, fmt(i.start), fmt(i.target), `${i.progress ?? 0}%`, i.deliveryLabel])}
      />
    </div>
  );
}
